/**
 * Componente Global de Detalle - Panel lateral
 *
 * Variante en panel lateral (Drawer) del `DetailModal`. Comparte la identidad
 * visual del detalle (header con gradiente verde-azul) pero se ancla a la
 * derecha con alto completo, siguiendo el patrón de drawers del sistema
 * (InventoryDrawer, ProductDrawer, etc.). Pensado para detalles densos que
 * necesitan más espacio y un header/footer siempre visibles.
 */

import React from 'react';
import { Drawer, Box, Typography, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { styled, alpha, SxProps, Theme } from '@mui/material/styles';
import { colors } from '../../../styles/theme/colors';
import { useTabScopedOpen } from '../../dashboard/TabPanelActiveContext';

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: 'min(1040px, 88vw)',
    },
    backgroundColor: theme.palette.background.paper,
    backgroundImage: 'none',
  },
}));

const DrawerHeaderBar = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: `linear-gradient(to right, ${colors.info.main}, ${colors.success.main})`,
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}));

const TitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: 0,
}));

const DrawerTitle = styled(Typography)(() => ({
  fontWeight: 600,
  fontSize: '1.25rem',
  lineHeight: 1.3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const HeaderActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexShrink: 0,
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: '#ffffff',
  '&:hover': {
    color: alpha(theme.palette.common.white, 0.85),
    backgroundColor: alpha(theme.palette.common.white, 0.12),
  },
}));

const DrawerBody = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
}));

const DrawerFooterBar = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.background.default : colors.grey[50],
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1.5),
}));

export interface DetailDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  icon?: React.ReactNode;
  /** Contenido opcional a la derecha del título (p. ej. chips de estado). */
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
  /** Botones del footer fijo. Si se omite, no se renderiza footer. */
  footerActions?: React.ReactNode;
  /** Ancho custom del panel (sobrescribe el responsivo por defecto). */
  width?: number | string;
  /** Se fusiona con los estilos base del Paper del Drawer. */
  paperSx?: SxProps<Theme>;
  className?: string;
}

/**
 * Panel de detalle unificado (variante lateral) para todo el sistema.
 *
 * - Header: gradiente verde-azul (misma identidad que `DetailModal`).
 * - Ancla: derecha, alto completo, ancho responsivo.
 * - Header y footer fijos; cuerpo con scroll.
 */
const DetailDrawer: React.FC<DetailDrawerProps> = ({
  open,
  onClose,
  title,
  icon,
  headerExtra,
  children,
  footerActions,
  width,
  paperSx,
  className,
}) => {
  const scopedOpen = useTabScopedOpen(open);

  return (
    <StyledDrawer
      anchor="right"
      open={scopedOpen}
      onClose={onClose}
      className={className}
      PaperProps={{
        sx: [
          width != null ? { width: { xs: '100%', md: width } } : {},
          ...(paperSx ? (Array.isArray(paperSx) ? paperSx : [paperSx]) : []),
        ],
      }}
    >
      <DrawerHeaderBar>
        <TitleContainer>
          {icon}
          <DrawerTitle>{title}</DrawerTitle>
        </TitleContainer>
        <HeaderActions>
          {headerExtra}
          <CloseButton onClick={onClose} aria-label="Cerrar">
            <CloseIcon />
          </CloseButton>
        </HeaderActions>
      </DrawerHeaderBar>

      <DrawerBody>{children}</DrawerBody>

      {footerActions ? <DrawerFooterBar>{footerActions}</DrawerFooterBar> : null}
    </StyledDrawer>
  );
};

export default DetailDrawer;
